import { useI18n } from "@common/hooks/i18n";
import date from "@common/lib/date";
import { getApi } from "@common/modules/data/api";
import { type Data } from "@common/modules/data/types";
import React from "react";
import { type GridConfigColumn } from "../../types";
import { getTotalSaleExtraColumns, totalSaleChartTypes } from "../lib";
import { type ProviderProps, type TotalSaleBase, type TotalSalesBaseRequest } from "../types";
import CommonStaticProvider from "./CommonStaticProvider";

export type TotalSalesByAccessCard = TotalSaleBase & {
  accessCardId: string;
  accessCardName: string;
  accessCardCount: number;
};

export type TotalSalesByAccessCardRequest = TotalSalesBaseRequest;
export type Props = Omit<ProviderProps, "id">;
export const SHOP_TOTAL_SALES_BY_ACCESS_CARD = "dd550458-aaa5-413c-ba14-5b06f5e28ac8";

const TotalSalesByAccessCardProvider = (props: Props) => {
  const i18n = useI18n();

  const api = React.useMemo(() => getApi(i18n), [i18n]);

  const fields = React.useMemo(
    (): Data.Api.FormField[] => [
      {
        formControlKey: "DateRange",
        formFieldId: "dateRange",
        formFieldKey: "Date",
        label: i18n.dateRange,
        plainTextValue: `{"from": "${date.extractDate(date.addMonth(new Date(), -1))}","to": "${date.extractDate(
          new Date()
        )}"}`
      }
    ],
    [i18n]
  );

  const getChartGridData = React.useCallback((id, data) => api.getTotalSalesByAccessCard(data as any), [api]);

  const getColumns = React.useCallback((): GridConfigColumn<TotalSalesByAccessCard>[] => {
    return [
      {
        dataType: "string",
        key: "accessCardName",
        label: i18n.accessCard
      }
    ];
  }, [i18n]);

  const extraColumns = React.useMemo(() => getTotalSaleExtraColumns(i18n), [i18n]);

  return (
    <CommonStaticProvider
      badges={["StartDate"]}
      category="Shop"
      fields={fields}
      isLoading={false}
      onLoaded={props.onLoaded}
    >
      {({ getBadges, getFilters, parameters }) =>
        props.children({
          id: SHOP_TOTAL_SALES_BY_ACCESS_CARD,
          chartTypes: totalSaleChartTypes,
          config: {
            aspectRatio: 2.5,
            colors: [],
            legend: "hide",
            xAxis: {
              key: "accessCardName"
            },
            yAxis: [{ key: "totalSales", color: "{$Blue}", label: i18n.totalSales }],
            totalRecords: 25,
            yNoData: "hide"
          },
          extraColumns,
          fields,
          getBadges,
          getChartData: getChartGridData,
          getFilters,
          gridColumns: getColumns,
          gridConfig: { columns: [], pageSize: 25, key: "accessCardId" },
          hasGrid: true,
          isLoading: false,
          name: i18n.totalSalesByAccessCard,
          parameters,
          type: "Bar"
        })
      }
    </CommonStaticProvider>
  );
};

export default TotalSalesByAccessCardProvider;
